import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, User } from "lucide-react";
import constants from "../constants/constants";

// --- CONFIG ---
const PDF_URL = constants.pdfDownload; // <-- must be a string

const Success: React.FC = () => {
  const navigate = useNavigate();

  const [fromStripe, setFromStripe] = useState(false);
  const [referrer, setReferrer] = useState<string | null>(null);
  const [prevPath, setPrevPath] = useState<string | null>(null);
  const [utmParams, setUtmParams] = useState<string>("");

  useEffect(() => {
    // --- Capture referrer ---
    const docReferrer = document.referrer || null;
    setReferrer(docReferrer);

    // --- Capture previous internal route (if you store it elsewhere) ---
    const storedPrevPath = sessionStorage.getItem("prevPath");
    setPrevPath(storedPrevPath);

    // --- Capture query params ---
    setUtmParams(window.location.search);

    // --- Stripe OR test override ---
    const params = new URLSearchParams(window.location.search);
    const hasStripeTestParam = params.get("stripe") === "y";

    const cameFromStripe =
      docReferrer !== null && docReferrer.includes("stripe.com");

    if (cameFromStripe || hasStripeTestParam) {
      setFromStripe(true);
    }

    // --- Debug logging (remove in prod) ---
    console.log("SUCCESS PAGE HIT:", {
      referrer: docReferrer,
      prevPath: storedPrevPath,
      utm: window.location.search,
      stripeReferrer: cameFromStripe,
      stripeTestOverride: hasStripeTestParam,
    });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-white py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle2 className="text-amber-700 w-12 h-12" />
          </div>

          {/* Heading */}
          <h1 className="text-3xl font-bold mb-4">Payment Successful 🎉</h1>
          <p className="text-lg text-gray-600 mb-8">
            Your purchase is complete. You can download your guide below.<br/><b>Don't leave this page without downloading the guide below!</b>
          </p>

          {/* Download Button */}
          <a
            href={fromStripe ? PDF_URL : undefined}
            target="_blank"
            rel="noopener noreferrer"
            download
            className={`inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold transition-all transform
              ${
                fromStripe
                  ? "bg-amber-600 text-white hover:bg-amber-700 hover:scale-105"
                  : "bg-gray-300 text-gray-400 cursor-not-allowed pointer-events-none"
              }`}
            aria-disabled={!fromStripe}
            tabIndex={fromStripe ? 0 : -1}
          >
            <User size={20} />
            Download Your Guide
          </a>

          {/* Locked notice */}
          {!fromStripe && (
            <p className="text-xs text-red-500 mt-4">
              Download unlocks after successful Stripe checkout.
            </p>
          )}

          {/* What’s inside */}
          <div className="bg-white rounded-xl p-6 shadow-sm mt-10 text-left">
            <h3 className="font-semibold mb-4 text-gray-900">
              What you’ll get:
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle2
                  className="text-amber-600 flex-shrink-0 mt-1"
                  size={20}
                />
                <span>
                  A clear, science-backed plan to slow hair loss and protect
                  existing hair
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2
                  className="text-amber-600 flex-shrink-0 mt-1"
                  size={20}
                />
                <span>
                  Daily actions with exact product guidance (no guesswork)
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2
                  className="text-amber-600 flex-shrink-0 mt-1"
                  size={20}
                />
                <span>
                  Printable checklist to stay consistent and track progress
                </span>
              </li>
            </ul>
          </div>

          {/* Debug info (DEV ONLY – remove in prod)
          <div className="mt-10 text-xs text-gray-400 text-left mx-auto max-w-lg">
            <div><strong>Referrer:</strong> {referrer || "(none)"}</div>
            <div><strong>Prev Path:</strong> {prevPath || "(none)"}</div>
            <div><strong>Query:</strong> {utmParams || "(none)"}</div>
          </div>
          */}
        </div>
      </div>
    </div>
  );
};

export default Success;
